import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
} from "@mui/material";
import { Delete, Edit, Plus } from 'lucide-react';
import {
    getViewBed,
    createBed,
    updateBed,
    deleteBed,
} from "../../../../../../api/services/hostService"; 
import { useUserContext } from "../../../../../protectedRoute/UserContext";
import { Button } from "../../../../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../../../../../components/ui/dialog";
import { Input } from "../../../../../../components/ui/input";
import { Label } from "../../../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../../components/ui/select";
import { Textarea } from "../../../../../../components/ui/textarea";

interface Bed {
    id?: number;
    property?: string;
    description?: string | null;
    is_booked?: boolean;
    co_roommate_gender?: string;
    price_per_bed?: number;
    bed_category?: string | null; // New field
    capacity?: number; // New field
}

interface CreateBedAvailabilityProps {
    open: boolean;
    onClose: () => void;
    onBedCreated: () => void;
}

const CreateBedAvailability: React.FC<CreateBedAvailabilityProps> = ({ open, onClose, onBedCreated }) => {
    const [editingBed, setEditingBed] = useState<Bed | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [customDescription, setCustomDescription] = useState("");
    const { state } = useUserContext();
    const { propertyID } = state;

    useEffect(() => {
        if (open) {
            setEditingBed({
                id: 0,
                co_roommate_gender: "",
                price_per_bed: undefined as any,
                bed_category: null,
                description: null,
                capacity: undefined as any,
            });
            setIsCreating(true);
            setShowErrors(false);
            setCustomDescription("");
        }
    }, [open]);

    const finalDescription = editingBed?.description === "Custom" ? customDescription.trim() : (editingBed?.description ?? "");

    const fieldErrors = {
        bed_category: !editingBed?.bed_category?.trim() ? "Category is required" : null,
        description: !editingBed?.description?.trim()
            ? "Description is required"
            : editingBed.description === "Custom" && !customDescription.trim()
            ? "Please enter a custom description"
            : null,
        capacity: !editingBed?.capacity || editingBed.capacity <= 0 ? "Capacity must be at least 1" : null,
        price_per_bed: !editingBed?.price_per_bed || editingBed.price_per_bed <= 0
            ? "Price is required"
            : editingBed.price_per_bed > 600
            ? "Price cannot exceed ₹600"
            : null,
        co_roommate_gender: !editingBed?.co_roommate_gender ? "Gender preference is required" : null,
    };

    const canSave = editingBed &&
    editingBed.co_roommate_gender &&
    editingBed.price_per_bed !== undefined && editingBed.price_per_bed > 0 && editingBed.price_per_bed <= 600 &&
    editingBed.description && editingBed.description.trim() !== '' &&
    (editingBed.description !== "Custom" || customDescription.trim() !== '') &&
    editingBed.bed_category && editingBed.bed_category.trim() !== '' &&
    editingBed.capacity !== undefined && editingBed.capacity > 0;

    const handleDialogClose = () => {
        onClose();
        setEditingBed(null);
        setIsCreating(false);
    };

    const handleSaveBed = async () => {
        setShowErrors(true);
        if (!canSave) return;
        if (editingBed && propertyID) {
          try {
            if (isCreating) {
              await createBed({
                property: propertyID,
                description: finalDescription,
                co_roommate_gender: editingBed.co_roommate_gender,
                price_per_bed: editingBed.price_per_bed,
                bed_category: editingBed.bed_category ?? '',
                capacity: editingBed.capacity,
              });
              onBedCreated();
            } else {
              await updateBed(editingBed.id!, {
                property: propertyID,
                description: finalDescription,
                co_roommate_gender: editingBed.co_roommate_gender,
                price_per_bed: editingBed.price_per_bed,
                bed_category: editingBed.bed_category ?? '',
                capacity: editingBed.capacity,
              });
              onBedCreated();
            }
          } catch (error) {
            console.error("Failed to save bed:", error);
          } finally {
            handleDialogClose();
          }
        }
      };

    const bedCategoryOptions = [
        "2 sharing-Male room",
        "3 sharing-Male room",
        "4 sharing-Male room",
        "2 sharing-Female room",
        "3 sharing-Female room",
        "4 sharing-Female room",
        "2 sharing-Unisex room",
        "3 sharing-Unisex room",
        "4 sharing-Unisex room",
    ];

    const categoryDescriptionMap: Record<string, string[]> = {
        "2 sharing-Male room": [
            "Well-ventilated bed in a 2-sharing male room with individual storage and study space.",
            "Comfortable 2-sharing male room with attached bathroom and daily housekeeping.",
            "Affordable 2-sharing male room with locker and WiFi — ideal for working professionals.",
        ],
        "3 sharing-Male room": [
            "Budget-friendly bed in a 3-sharing male room with individual lockers and common bathroom.",
            "Cozy 3-sharing male room with bunk beds and WiFi — perfect for students.",
            "Economical 3-sharing male room with 24/7 security, ideal for working professionals.",
        ],
        "4 sharing-Male room": [
            "Economical bed in a 4-sharing male dorm with storage locker and fan — great for students.",
            "Budget 4-sharing male room with bunk beds, WiFi, and shared bathroom facilities.",
            "Affordable 4-sharing male dorm with CCTV security and common amenities.",
        ],
        "2 sharing-Female room": [
            "Secure and comfortable bed in a 2-sharing female room with CCTV and 24/7 security.",
            "Well-lit 2-sharing female room with attached bathroom, wardrobe, and study table.",
            "Safe 2-sharing female room with individual lockers in a gated, ladies-only property.",
        ],
        "3 sharing-Female room": [
            "Safe 3-sharing female room with individual lockers, WiFi, and 24/7 female security.",
            "Budget-friendly 3-sharing female room with common bathroom and daily housekeeping.",
            "Cozy 3-sharing female dorm with bunk beds, locker, and CCTV surveillance.",
        ],
        "4 sharing-Female room": [
            "Economical 4-sharing female dorm with storage lockers, WiFi, and round-the-clock security.",
            "Affordable 4-sharing female room with bunk beds, common bathroom, and daily cleaning.",
            "Budget 4-sharing female room with CCTV, locker, and common kitchen access.",
        ],
        "2 sharing-Unisex room": [
            "Modern 2-sharing unisex room with individual storage, WiFi, and 24/7 security.",
            "Comfortable 2-sharing unisex room with partition for privacy and attached bathroom.",
            "Spacious 2-sharing unisex room with study desk, locker, and daily housekeeping.",
        ],
        "3 sharing-Unisex room": [
            "Affordable 3-sharing unisex room with bunk beds, individual lockers, and WiFi.",
            "Budget 3-sharing unisex room with ceiling fan, common bathroom, and 24/7 security.",
            "Cozy 3-sharing unisex dorm ideal for students and young working professionals.",
        ],
        "4 sharing-Unisex room": [
            "Economical 4-sharing unisex dorm with bunk beds, storage, WiFi, and common facilities.",
            "Budget-friendly 4-sharing unisex room with CCTV security and common amenities.",
            "Affordable 4-sharing unisex dorm with lockers, fan, and access to shared kitchen.",
        ],
    };

    const availableDescriptions = editingBed?.bed_category
        ? [...(categoryDescriptionMap[editingBed.bed_category] ?? []), "Custom"]
        : [];

    return (
        <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Plus className="h-5 w-5 text-primary" />
              {isCreating ? "Add New Bed" : "Update Bed"}
            </DialogTitle>
            <DialogDescription>
              {isCreating ? "Add your bed details and set availability to start earning" : "Update your bed details"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            <div className="space-y-2">
                <Label htmlFor="category">Category <span className="text-red-500">*</span></Label>
                <Select value={editingBed?.bed_category || ""} onValueChange={(v) => {
                    if(editingBed) setEditingBed({...editingBed, bed_category: v, description: null});
                    setCustomDescription("");
                }}>
                  <SelectTrigger className={`bg-background ${showErrors && fieldErrors.bed_category ? "border-red-500" : ""}`}>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border z-50">
                    {bedCategoryOptions.map(option => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
                {showErrors && fieldErrors.bed_category && <p className="text-red-500 text-xs">{fieldErrors.bed_category}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description <span className="text-red-500">*</span></Label>
                <Select
                    value={editingBed?.description || ""}
                    onValueChange={(v) => {
                        if (editingBed) setEditingBed({...editingBed, description: v});
                        if (v !== "Custom") setCustomDescription("");
                    }}
                    disabled={!editingBed?.bed_category}
                >
                  <SelectTrigger className={`bg-background ${showErrors && fieldErrors.description ? "border-red-500" : ""}`}>
                    <SelectValue placeholder={editingBed?.bed_category ? "Select description" : "Select a category first"} />
                  </SelectTrigger>
                  <SelectContent className="bg-card border z-50">
                    {availableDescriptions.map(option => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
                {editingBed?.description === "Custom" && (
                  <Textarea
                    placeholder="Enter custom description..."
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    className="bg-background resize-none mt-2"
                    rows={3}
                  />
                )}
                {!editingBed?.bed_category && <p className="text-xs text-muted-foreground">Choose a category above to see relevant descriptions.</p>}
                {showErrors && fieldErrors.description && <p className="text-red-500 text-xs">{fieldErrors.description}</p>}
              </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity <span className="text-red-500">*</span></Label>
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  placeholder="e.g., 2"
                  value={editingBed?.capacity ?? ""}
                  onChange={(e) => {
                      if(editingBed) setEditingBed({...editingBed, capacity: +e.target.value});
                    }}
                  className={`bg-background ${showErrors && fieldErrors.capacity ? "border-red-500" : ""}`}
                />
                {showErrors && fieldErrors.capacity && <p className="text-red-500 text-xs">{fieldErrors.capacity}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price per night (₹) <span className="text-red-500">*</span></Label>
                <Input
                  id="price"
                  type="number"
                  placeholder="e.g., 400"
                  value={editingBed?.price_per_bed ?? ""}
                  onChange={(e) => {
                        if(editingBed) setEditingBed({...editingBed, price_per_bed: +e.target.value});
                    }}
                  className={`bg-background ${showErrors && fieldErrors.price_per_bed ? "border-red-500" : ""}`}
                />
                {showErrors && fieldErrors.price_per_bed && <p className="text-red-500 text-xs">{fieldErrors.price_per_bed}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Gender Preference <span className="text-red-500">*</span></Label>
              <div className="flex gap-2">
                {["male", "female", "unisex"].map((g) => (
                  <Button
                    key={g}
                    type="button"
                    variant={editingBed?.co_roommate_gender === g ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                        if(editingBed) setEditingBed({...editingBed, co_roommate_gender: g});
                    }}
                    className="flex-1 capitalize"
                  >
                    {g}
                  </Button>
                ))}
              </div>
              {showErrors && fieldErrors.co_roommate_gender && <p className="text-red-500 text-xs">{fieldErrors.co_roommate_gender}</p>}
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleDialogClose}>
              Cancel
            </Button>
            <Button onClick={handleSaveBed}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
};

export default CreateBedAvailability;